import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Building2, Users, DollarSign, TrendingUp, Plus, Search,
  Eye, Edit, UserPlus, MoreVertical, ChevronLeft, ChevronRight,
  Loader2, Palette, ExternalLink, LogIn, AlertCircle, CreditCard,
  Copy, Check, Link2, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { toast } from 'sonner';

const ResellerDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const _tabParam = searchParams.get('tab');
  const activeTab = _tabParam === 'billing' ? 'billing' : (_tabParam === 'customers' ? 'customers' : 'overview');
  const [dashboard, setDashboard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  // Sending domain (Phase 2)
  const [emailDomain, setEmailDomain] = useState(null);
  const [domainThreshold, setDomainThreshold] = useState(50);
  const [domainInput, setDomainInput] = useState('');
  const [domainBusy, setDomainBusy] = useState(false);
  
  // Modal states
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [showCustomerDetail, setShowCustomerDetail] = useState(false);
  const [showBranding, setShowBranding] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [checkoutCustomer, setCheckoutCustomer] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // Create customer form
  const [customerForm, setCustomerForm] = useState({
    company_name: '',
    admin_email: '',
    admin_password: '',
    admin_name: '',
    plan_level: 'starter',
    custom_price: '',
    notes: ''
  });

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/reseller/dashboard');
      setDashboard(res.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    }
  }, []);

  const fetchEmailDomain = useCallback(async () => {
    try {
      const res = await api.get('/reseller/email-domain');
      setEmailDomain(res.data.email_domain);
      setDomainThreshold(res.data.threshold || 50);
    } catch (error) { /* non-blocking */ }
  }, []);

  const handleSetupDomain = async () => {
    const domain = domainInput.trim().toLowerCase();
    if (!domain || !domain.includes('.')) { toast.error('Enter a valid domain, e.g. theagency.com'); return; }
    setDomainBusy(true);
    try {
      const res = await api.post('/reseller/email-domain', { domain, subdomain: 'mail' });
      setEmailDomain(res.data.email_domain);
      toast.success('Sending domain created — add the DNS records, then verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set up sending domain');
    } finally { setDomainBusy(false); }
  };

  const handleVerifyDomain = async () => {
    setDomainBusy(true);
    try {
      const res = await api.post('/reseller/email-domain/verify');
      setEmailDomain(res.data.email_domain);
      toast[res.data.verified ? 'success' : 'error'](res.data.verified ? 'Sending domain verified!' : 'DNS records not found yet — allow time for propagation and retry');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally { setDomainBusy(false); }
  };

  const fetchCustomers = useCallback(async () => {
    try {
      setCustomersLoading(true);
      const params = new URLSearchParams({ page, per_page: 10 });
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await api.get(`/reseller/customers?${params}`);
      setCustomers(res.data.customers);
      setTotalPages(res.data.total_pages);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setCustomersLoading(false);
    }
  }, [page, searchQuery]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchDashboard();
      await fetchCustomers();
      fetchEmailDomain();
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchCustomers, fetchEmailDomain]);

  const handleCreateCustomer = async () => {
    if (!customerForm.company_name || !customerForm.admin_email || !customerForm.admin_password || !customerForm.admin_name) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      setCreating(true);
      const payload = {
        ...customerForm,
        custom_price: customerForm.custom_price ? parseFloat(customerForm.custom_price) : null
      };
      await api.post('/reseller/customers', payload);
      toast.success('Customer created successfully');
      setShowCreateCustomer(false);
      setCustomerForm({
        company_name: '',
        admin_email: '',
        admin_password: '',
        admin_name: '',
        plan_level: 'starter',
        custom_price: '',
        notes: ''
      });
      fetchDashboard();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setCreating(false);
    }
  };

  const handleViewCustomer = async (customer) => {
    try {
      const res = await api.get(`/reseller/customers/${customer.tenant_id}`);
      setSelectedCustomer(res.data);
      setShowCustomerDetail(true);
    } catch (error) {
      toast.error('Failed to load customer details');
    }
  };

  const handleImpersonate = async (tenantId) => {
    try {
      const res = await api.post(`/reseller/customers/${tenantId}/impersonate`);
      // Store the impersonation token and redirect
      localStorage.setItem('impersonation_token', res.data.access_token);
      localStorage.setItem('original_token', localStorage.getItem('token'));
      localStorage.setItem('token', res.data.access_token);
      toast.success(`Now impersonating ${res.data.user.email}`);
      window.location.href = '/pages';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate customer');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'trial':
        return <Badge className="bg-blue-100 text-blue-700">Trial</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin text-slate-400" size={48} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 min-w-0 p-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              {dashboard?.reseller?.branding?.logo_url ? (
                <img 
                  src={dashboard.reseller.branding.logo_url} 
                  alt="Logo" 
                  className="h-10"
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: dashboard?.reseller?.branding?.primary_color || '#4F46E5' }}
                >
                  {dashboard?.reseller?.name?.charAt(0) || 'R'}
                </div>
              )}
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {dashboard?.reseller?.branding?.company_name || dashboard?.reseller?.name || 'Reseller Dashboard'}
              </h1>
            </div>
            <p className="text-slate-500">Manage your customers and track your earnings</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowBranding(true)}>
              <Palette size={18} className="mr-2" />
              Branding
            </Button>
            <Button onClick={() => setShowCreateCustomer(true)}>
              <Plus size={18} className="mr-2" />
              Add Customer
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Users className="text-indigo-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Customers</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {dashboard?.stats?.customer_count || 0}
                    <span className="text-sm font-normal text-slate-400">
                      /{dashboard?.stats?.max_customers || 50}
                    </span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="text-green-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Active</p>
                  <p className="text-2xl font-bold text-slate-900">{dashboard?.stats?.active_customers || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="text-amber-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pending Payout</p>
                  <p className="text-2xl font-bold text-slate-900">
                    ${dashboard?.stats?.pending_payout?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="text-purple-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Earned</p>
                  <p className="text-2xl font-bold text-slate-900">
                    ${dashboard?.stats?.total_earned?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setSearchParams(v === 'overview' ? {} : { tab: v })}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="reseller-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="customers" data-testid="reseller-tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="billing" data-testid="reseller-tab-billing">Billing & Earnings</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-6" data-testid="reseller-overview">
            {/* Commission & bonus tiers (read-only for the reseller) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Commission</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-6 mb-4">
                  <div>
                    <p className="text-xs text-slate-500">Current rate (based on your MRR)</p>
                    <p className="text-3xl font-bold text-slate-900" data-testid="overview-effective-rate">{dashboard?.stats?.effective_commission_rate ?? dashboard?.base_commission_rate ?? 50}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Your MRR</p>
                    <p className="text-3xl font-bold text-slate-900" data-testid="overview-mrr">${(dashboard?.stats?.mrr || 0).toFixed(2)}</p>
                  </div>
                </div>
                <p className="text-xs font-medium text-slate-600 mb-2">Bonus tiers (your share grows with monthly recurring revenue)</p>
                <div className="space-y-1" data-testid="overview-tiers">
                  {(dashboard?.revenue_tiers || []).map((t, i) => {
                    const active = (dashboard?.stats?.effective_commission_rate) === t.percentage;
                    return (
                      <div key={i} className={`flex items-center justify-between text-sm px-3 py-2 rounded-md ${active ? 'bg-indigo-50 border border-indigo-200' : 'bg-slate-50'}`}>
                        <span className="text-slate-700">${t.min.toLocaleString()} – {t.max === -1 ? '∞' : '$' + t.max.toLocaleString()} MRR</span>
                        <span className="font-semibold text-slate-900">{t.percentage}%{active && <span className="ml-2 text-xs text-indigo-600">● current</span>}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Sending Domain (Phase 2) — verify to unlock large campaigns */}
            <Card data-testid="reseller-sending-domain">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  Sending Domain
                  {emailDomain?.verified ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium" data-testid="domain-verified-badge">Verified</span>
                  ) : emailDomain ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" data-testid="domain-pending-badge">Pending DNS</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">Not set up</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500 mb-3">
                  Newsletters send from your own domain once verified. Campaigns to more than {domainThreshold} recipients require a verified sending domain.
                </p>

                {!emailDomain && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={domainInput}
                      onChange={(e) => setDomainInput(e.target.value)}
                      placeholder="theagency.com"
                      className="flex-1 text-sm px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      data-testid="sending-domain-input"
                    />
                    <button onClick={handleSetupDomain} disabled={domainBusy} className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white disabled:opacity-40 hover:bg-indigo-700 transition-colors" data-testid="setup-domain-btn">
                      {domainBusy ? 'Setting up…' : 'Set up domain'}
                    </button>
                  </div>
                )}

                {emailDomain && (
                  <div className="space-y-3">
                    <p className="text-sm"><span className="text-slate-500">Domain:</span> <span className="font-medium">{emailDomain.subdomain}.{emailDomain.domain}</span></p>
                    {!emailDomain.verified && (emailDomain.records || []).length > 0 && (
                      <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="domain-dns-records">
                        <div className="bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">Add these DNS records at your domain registrar</div>
                        <div className="divide-y text-xs">
                          {(emailDomain.records || []).map((r, i) => (
                            <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 items-center">
                              <span className="col-span-2 font-mono text-slate-500">{r.type}</span>
                              <span className="col-span-4 font-mono break-all">{r.host}</span>
                              <span className="col-span-5 font-mono break-all text-slate-600">{r.data}</span>
                              <span className="col-span-1 text-right">{r.valid ? '✅' : '⏳'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {!emailDomain.verified && (
                      <button onClick={handleVerifyDomain} disabled={domainBusy} className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white disabled:opacity-40 hover:bg-green-700 transition-colors" data-testid="verify-domain-btn">
                        {domainBusy ? 'Checking…' : "I've added the records — Verify"}
                      </button>
                    )}
                    {emailDomain.verified && (
                      <p className="text-sm text-green-700">Your domain is verified. Newsletters now send from {emailDomain.subdomain}.{emailDomain.domain} and large campaigns are unlocked.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Recent Customers</CardTitle>
                <Button variant="outline" size="sm" onClick={() => setSearchParams({ tab: 'customers' })} data-testid="overview-view-all-customers">View all</Button>
              </CardHeader>
              <CardContent>
                {(dashboard?.recent_customers || []).length === 0 ? (
                  <p className="text-sm text-slate-500">No customers yet. Add your first customer from the Customers tab.</p>
                ) : (
                  <div className="divide-y">
                    {(dashboard?.recent_customers || []).map((c, i) => (
                      <div key={i} className="flex items-center justify-between py-3">
                        <div>
                          <p className="font-medium text-slate-900">{c.name || c.tenant_id}</p>
                          <p className="text-xs text-slate-500">{c.subdomain ? `${c.subdomain}` : ''}</p>
                        </div>
                        {getStatusBadge(c.subscription_status)}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="customers" className="mt-6">
            {/* Search */}
            <div className="flex gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  placeholder="Search customers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Customers Table */}
            <Card>
              <CardContent className="p-0">
                {customersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin text-slate-400" size={32} />
                  </div>
                ) : customers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="mx-auto text-slate-300 mb-4" size={48} />
                    <h3 className="text-lg font-medium text-slate-600">No customers yet</h3>
                    <p className="text-slate-400 mt-1">Create your first customer to get started</p>
                    <Button onClick={() => setShowCreateCustomer(true)} className="mt-4 gap-2">
                      <Plus size={18} />
                      Add Customer
                    </Button>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Customer</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Plan</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Price</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Users</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Created</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {customers.map((customer) => (
                        <tr key={customer.tenant_id} className="hover:bg-slate-50">
                          <td className="py-4 px-4">
                            <div>
                              <p className="font-medium text-slate-900">{customer.name}</p>
                              {customer.reseller_settings?.notes && (
                                <p className="text-sm text-slate-400">{customer.reseller_settings.notes}</p>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            {getStatusBadge(customer.subscription_status)}
                          </td>
                          <td className="py-4 px-4">
                            <Badge variant="outline" className="capitalize">{customer.plan_level}</Badge>
                          </td>
                          <td className="py-4 px-4">
                            {customer.reseller_settings?.custom_price 
                              ? `$${customer.reseller_settings.custom_price}/mo`
                              : '-'
                            }
                          </td>
                          <td className="py-4 px-4">{customer.user_count || 0}</td>
                          <td className="py-4 px-4 text-slate-500">
                            {new Date(customer.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical size={16} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewCustomer(customer)}>
                                  <Eye size={16} className="mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  setCheckoutCustomer(customer);
                                  setShowCheckout(true);
                                }}>
                                  <CreditCard size={16} className="mr-2" />
                                  Create Checkout
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleImpersonate(customer.tenant_id)}>
                                  <LogIn size={16} className="mr-2" />
                                  Impersonate Customer
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  <Edit size={16} className="mr-2" />
                                  Edit
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4">
                <p className="text-sm text-slate-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="billing" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Earnings & Commissions</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => window.location.href = '/reseller/billing'}>
                    View Full Billing
                    <ExternalLink size={14} className="ml-2" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Your Commission Rate</p>
                    <p className="text-3xl font-bold text-green-600">
                      {dashboard?.stats?.commission_rate || 40}-60%
                    </p>
                    <p className="text-sm text-slate-400 mt-1">Tiered based on total revenue</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Pending Payout</p>
                    <p className="text-3xl font-bold text-amber-600">
                      ${dashboard?.stats?.pending_payout?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      <a href="/reseller/billing" className="text-indigo-600 hover:underline">Request payout →</a>
                    </p>
                  </div>
                </div>
                
                {(!dashboard?.stats?.total_earned || dashboard?.stats?.total_earned === 0) && (
                  <Alert className="mt-6">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      No earnings yet. Use "Create Checkout" from the customer menu to generate payment links for your customers.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Customer Modal */}
        <Dialog open={showCreateCustomer} onOpenChange={setShowCreateCustomer}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="text-indigo-600" size={24} />
                Add New Customer
              </DialogTitle>
              <DialogDescription>
                Create a new customer account. They will receive their login credentials.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  placeholder="Acme Corporation"
                  value={customerForm.company_name}
                  onChange={(e) => setCustomerForm({...customerForm, company_name: e.target.value})}
                />
              </div>

              <div className="border-t pt-4">
                <p className="font-medium text-slate-700 mb-3">Admin Account</p>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Admin Name *</Label>
                    <Input
                      placeholder="Jane Smith"
                      value={customerForm.admin_name}
                      onChange={(e) => setCustomerForm({...customerForm, admin_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Admin Email *</Label>
                    <Input
                      type="email"
                      placeholder="jane@acme.com"
                      value={customerForm.admin_email}
                      onChange={(e) => setCustomerForm({...customerForm, admin_email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={customerForm.admin_password}
                      onChange={(e) => setCustomerForm({...customerForm, admin_password: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Plan Level</Label>
                  <Select 
                    value={customerForm.plan_level} 
                    onValueChange={(v) => setCustomerForm({...customerForm, plan_level: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Custom Price ($/mo)</Label>
                  <Input
                    type="number"
                    placeholder="99.00"
                    value={customerForm.custom_price}
                    onChange={(e) => setCustomerForm({...customerForm, custom_price: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Internal Notes</Label>
                <Input
                  placeholder="VIP customer, special support..."
                  value={customerForm.notes}
                  onChange={(e) => setCustomerForm({...customerForm, notes: e.target.value})}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateCustomer(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCustomer} disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Creating...
                  </>
                ) : (
                  'Create Customer'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Customer Detail Modal */}
        <Dialog open={showCustomerDetail} onOpenChange={setShowCustomerDetail}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedCustomer?.name}</DialogTitle>
            </DialogHeader>
            
            {selectedCustomer && (
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-slate-500">Pages</p>
                      <p className="text-2xl font-bold">{selectedCustomer.page_count || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-slate-500">Users</p>
                      <p className="text-2xl font-bold">{selectedCustomer.users?.length || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-slate-500">Newsletters</p>
                      <p className="text-2xl font-bold">{selectedCustomer.newsletter_count || 0}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Users List */}
                {selectedCustomer.users?.length > 0 && (
                  <div>
                    <p className="font-medium text-slate-700 mb-2">Team Members</p>
                    <div className="space-y-2">
                      {selectedCustomer.users.map((user) => (
                        <div key={user.user_id} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{user.full_name}</p>
                            <p className="text-sm text-slate-500">{user.email}</p>
                          </div>
                          <Badge variant="outline" className="capitalize">{user.role}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Settings */}
                {selectedCustomer.reseller_settings && (
                  <div>
                    <p className="font-medium text-slate-700 mb-2">Your Settings</p>
                    <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                      {selectedCustomer.reseller_settings.custom_price && (
                        <p className="text-sm">
                          <span className="text-slate-500">Custom Price:</span>{' '}
                          <span className="font-medium">${selectedCustomer.reseller_settings.custom_price}/mo</span>
                        </p>
                      )}
                      {selectedCustomer.reseller_settings.notes && (
                        <p className="text-sm">
                          <span className="text-slate-500">Notes:</span>{' '}
                          {selectedCustomer.reseller_settings.notes}
                        </p>
                      )}
                      <p className="text-sm">
                        <span className="text-slate-500">Acquired:</span>{' '}
                        {new Date(selectedCustomer.reseller_settings.acquired_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCustomerDetail(false)}>
                Close
              </Button>
              <Button onClick={() => selectedCustomer && handleImpersonate(selectedCustomer.tenant_id)}>
                <LogIn size={16} className="mr-2" />
                Impersonate Customer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Checkout Modal */}
        <CheckoutModal
          open={showCheckout}
          onClose={() => { setShowCheckout(false); setCheckoutCustomer(null); }}
          customer={checkoutCustomer}
        />

        {/* Branding Modal */}
        <BrandingModal 
          open={showBranding} 
          onClose={() => setShowBranding(false)} 
          currentBranding={dashboard?.reseller?.branding || {}}
          onSaved={(newBranding) => {
            setDashboard({
              ...dashboard,
              reseller: {
                ...dashboard.reseller,
                branding: newBranding
              }
            });
          }}
        />
      </div>
    </div>
  );
};

// Checkout Modal Component
const CheckoutModal = ({ open, onClose, customer }) => {
  const [submitting, setSubmitting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    plan_name: 'Pro',
    amount: '',
    billing_period: 'monthly',
  });

  React.useEffect(() => {
    if (open && customer) {
      setCheckoutResult(null);
      setCopied(false);
      setEmailSent(false);
      setForm({
        plan_name: 'Pro',
        amount: customer.reseller_settings?.custom_price || '',
        billing_period: 'monthly',
      });
    }
  }, [open, customer]);

  const handleSubmit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/reseller/customers/${customer.tenant_id}/checkout`, {
        plan_name: form.plan_name,
        amount: parseFloat(form.amount),
        billing_period: form.billing_period,
      });
      setCheckoutResult(res.data);
      toast.success('Checkout link created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = () => {
    if (checkoutResult?.checkout_url) {
      navigator.clipboard.writeText(checkoutResult.checkout_url);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await api.post(`/reseller/customers/${customer.tenant_id}/send-checkout-email`, {
        checkout_url: checkoutResult.checkout_url,
        plan_name: form.plan_name,
        amount: parseFloat(form.amount),
        billing_period: form.billing_period,
      });
      setEmailSent(true);
      toast.success('Invoice email sent to customer!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="text-indigo-600" size={24} />
            Create Checkout
          </DialogTitle>
          <DialogDescription>
            Generate a Stripe payment link for <strong>{customer?.name}</strong>
          </DialogDescription>
        </DialogHeader>

        {!checkoutResult ? (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Plan Name</Label>
                <Select value={form.plan_name} onValueChange={(v) => setForm({ ...form, plan_name: v })}>
                  <SelectTrigger data-testid="checkout-plan-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Starter">Starter</SelectItem>
                    <SelectItem value="Pro">Pro</SelectItem>
                    <SelectItem value="Enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Amount (USD)</Label>
                <Input
                  data-testid="checkout-amount-input"
                  type="number"
                  placeholder="99.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Billing Period</Label>
                <Select value={form.billing_period} onValueChange={(v) => setForm({ ...form, billing_period: v })}>
                  <SelectTrigger data-testid="checkout-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button data-testid="checkout-submit-btn" onClick={handleSubmit} disabled={submitting}>
                {submitting ? (
                  <><Loader2 className="animate-spin mr-2" size={16} />Creating...</>
                ) : (
                  'Generate Checkout Link'
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="py-4 space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Check className="text-green-600" size={20} />
                <p className="font-medium text-green-800">Checkout Link Ready</p>
              </div>
              <p className="text-sm text-green-700 mb-3">
                Send this link to your customer. They'll see your brand name on their bank statement.
              </p>
              <div className="flex gap-2">
                <Input
                  data-testid="checkout-url-input"
                  readOnly
                  value={checkoutResult.checkout_url}
                  className="text-xs bg-white"
                />
                <Button variant="outline" size="icon" onClick={handleCopy} data-testid="checkout-copy-btn">
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button
                data-testid="checkout-send-email-btn"
                variant={emailSent ? "outline" : "default"}
                onClick={handleSendEmail}
                disabled={sendingEmail || emailSent}
                className={emailSent ? "border-green-300 text-green-700" : ""}
              >
                {sendingEmail ? (
                  <><Loader2 className="animate-spin mr-2" size={16} />Sending...</>
                ) : emailSent ? (
                  <><Check size={16} className="mr-2" />Email Sent</>
                ) : (
                  <><Mail size={16} className="mr-2" />Send via Email</>
                )}
              </Button>
              <Button
                data-testid="checkout-open-btn"
                variant="outline"
                onClick={() => window.open(checkoutResult.checkout_url, '_blank')}
              >
                <ExternalLink size={16} className="mr-2" />
                Open Checkout
              </Button>
            </div>

            <div className="flex justify-end">
              <Button variant="ghost" onClick={onClose}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Branding Modal Component
const BrandingModal = ({ open, onClose, currentBranding, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({
    company_name: '',
    logo_url: '',
    favicon_url: '',
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    support_email: '',
    custom_domain: '',
  });

  // Initialize form when modal opens
  React.useEffect(() => {
    if (open && currentBranding) {
      setBranding({
        company_name: currentBranding.company_name || '',
        logo_url: currentBranding.logo_url || '',
        favicon_url: currentBranding.favicon_url || '',
        primary_color: currentBranding.primary_color || '#4F46E5',
        secondary_color: currentBranding.secondary_color || '#7C3AED',
        support_email: currentBranding.support_email || '',
        custom_domain: currentBranding.custom_domain || '',
      });
    }
  }, [open, currentBranding]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/reseller/branding', branding);
      toast.success('Branding updated successfully!');
      onSaved?.(res.data.branding);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="text-indigo-600" size={24} />
            Customize Branding
          </DialogTitle>
          <DialogDescription>
            Customize how your portal appears to your customers
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Company Name *</Label>
            <Input 
              placeholder="Your Agency CMS" 
              value={branding.company_name}
              onChange={(e) => setBranding({ ...branding, company_name: e.target.value })}
            />
            <p className="text-xs text-slate-500">Shown in the header and login page</p>
          </div>

          <div className="space-y-2">
            <Label>Logo URL</Label>
            <Input 
              placeholder="https://example.com/logo.png" 
              value={branding.logo_url}
              onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
            />
            {branding.logo_url && (
              <div className="mt-2 p-2 bg-slate-100 rounded">
                <img src={branding.logo_url} alt="Logo preview" className="h-8 object-contain" />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Favicon URL</Label>
            <Input 
              placeholder="https://example.com/favicon.ico" 
              value={branding.favicon_url}
              onChange={(e) => setBranding({ ...branding, favicon_url: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Primary Color</Label>
              <div className="flex gap-2">
                <Input 
                  value={branding.primary_color}
                  onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                />
                <input
                  type="color"
                  value={branding.primary_color}
                  onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Secondary Color</Label>
              <div className="flex gap-2">
                <Input 
                  value={branding.secondary_color}
                  onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                />
                <input
                  type="color"
                  value={branding.secondary_color}
                  onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Support Email</Label>
            <Input 
              type="email"
              placeholder="support@youragency.com" 
              value={branding.support_email}
              onChange={(e) => setBranding({ ...branding, support_email: e.target.value })}
            />
            <p className="text-xs text-slate-500">Shown on login page for customer support</p>
          </div>

          <div className="space-y-2">
            <Label>Custom Domain (optional)</Label>
            <Input 
              placeholder="cms.youragency.com" 
              value={branding.custom_domain}
              onChange={(e) => setBranding({ ...branding, custom_domain: e.target.value })}
            />
            <p className="text-xs text-slate-500">Contact support to set up DNS for custom domains</p>
          </div>

          {/* Preview Section */}
          <div className="mt-4 p-4 bg-slate-900 rounded-lg">
            <p className="text-xs text-slate-400 mb-2">Preview</p>
            <div className="flex items-center gap-3">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="Logo" className="h-6 object-contain" />
              ) : (
                <span className="text-white font-bold" style={{ color: branding.primary_color }}>
                  {branding.company_name || 'Your Brand'}
                </span>
              )}
              <div className="flex gap-1 ml-auto">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: branding.primary_color }}></div>
                <div className="w-4 h-4 rounded" style={{ backgroundColor: branding.secondary_color }}></div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !branding.company_name}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Branding'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ResellerDashboard;
